// The sync engine (Guide §10.3–10.8). One loop per signed-in shop: push the outbox (oldest first, ≤100 per request), poll parked
// operations, pull what changed on the server, and fold acknowledged operations into the mirror. Triggers: app open, `online`, 2 s
// after a write, every 30 s. Failures back off with jitter up to 5 minutes. Web Locks keep two tabs from syncing at once.
import { SYNC_DEBOUNCE_MS, SYNC_INTERVAL_MS, SYNC_MAX_OPS_PER_PUSH, SYNC_PULL_DEFAULT_LIMIT, SYNC_RETRY_MAX_MS, SNAPSHOT_ENTITIES, type PullResponse, type PushResponse } from '@dd/shared';
import { api, ApiError, onApiEvent } from '@/lib/api';
import { db, hasDb, metaGet, metaSet } from '@/db';
import { ENTITY_STORES } from '@/db/schema';
import type { OutboxItem } from '@/db/types';
import { loadClock, sampleServerTime } from '@/lib/clock';
import { ApplyCtx } from './ctx';
import { APPLY, GUARDS } from './apply';
import { recomputeOverlay } from './overlay';
import { registerKick, revertOp } from './ops';
import { syncContext, hasSyncContext } from './context';
import { setSync } from './status';
import { refreshCounts } from './counts';
import { normalizeRow } from './normalize';
import { bizDate } from '@dd/shared';

const SENT_LOG_MS = 24 * 3_600_000;

class Engine {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private busy = false;
  private again = false;
  private failures = 0;
  private offs: Array<() => void> = [];
  private pausedForAuth = false;

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.pausedForAuth = false;
    await loadClock();
    registerKick((d) => this.kick(d));
    const online = () => { setSync({ online: true }); this.kick(300); };
    const offline = () => setSync({ online: false });
    const vis = () => { if (document.visibilityState === 'visible') this.kick(500); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', vis);
    this.offs = [
      () => window.removeEventListener('online', online), () => window.removeEventListener('offline', offline), () => document.removeEventListener('visibilitychange', vis),
      onApiEvent((e) => {
        if (e.upgradeRequired) setSync({ needsUpgrade: true });
        if (e.unauthenticated) this.pausedForAuth = true;
        if (e.epoch) void this.noteEpoch(e.epoch);
      }),
    ];
    this.interval = setInterval(() => { if (document.visibilityState === 'visible') this.kick(0); }, SYNC_INTERVAL_MS);
    setSync({ online: navigator.onLine });
    await db().outbox.where('state').equals('sending').modify({ state: 'pending' }); // a crash mid-push: the server de-duplicates by opId
    await refreshCounts();
    const first = (await metaGet<number>('lastSeq')) === undefined;
    setSync({ initial: first });
    this.kick(0);
  }

  stop(): void {
    this.running = false;
    this.offs.forEach((f) => f());
    this.offs = [];
    if (this.timer) clearTimeout(this.timer);
    if (this.interval) clearInterval(this.interval);
    this.timer = null;
    this.interval = null;
  }

  resumeAfterLogin(): void { this.pausedForAuth = false; this.failures = 0; this.kick(0); }

  kick(delay = SYNC_DEBOUNCE_MS): void {
    if (!this.running) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.run(), Math.max(0, delay));
  }

  /** Awaitable one-shot used by “sync now” buttons and tests. */
  async syncNow(): Promise<void> {
    if (!this.running) return;
    await this.run();
  }

  private async run(): Promise<void> {
    if (!this.running || !hasDb() || !hasSyncContext()) return;
    if (this.busy) { this.again = true; return; }
    if (!navigator.onLine || this.pausedForAuth) return;
    this.busy = true;
    const shopId = syncContext().shopId;
    const work = async () => {
      setSync({ syncing: true });
      try {
        await this.cycle();
        this.failures = 0;
        setSync({ lastError: null, lastSyncAt: Date.now(), initial: false });
      } catch (e) {
        this.failures++;
        const code = e instanceof ApiError ? e.code : 'error';
        setSync({ lastError: code });
        if (e instanceof ApiError && e.status === 0) setSync({ online: navigator.onLine });
        const backoff = Math.min(SYNC_RETRY_MAX_MS, 2000 * 2 ** Math.min(this.failures, 8));
        this.kick(backoff * (0.7 + Math.random() * 0.6));
      } finally {
        setSync({ syncing: false });
        await refreshCounts();
      }
    };
    try {
      if ('locks' in navigator) {
        await navigator.locks.request(`dd-sync-${shopId}`, { ifAvailable: true }, async (lock) => { if (lock) await work(); });
      } else await work();
    } finally {
      this.busy = false;
      if (this.again) { this.again = false; this.kick(200); }
    }
  }

  private async cycle(): Promise<void> {
    if ((await metaGet<boolean>('needSnapshot')) || (await metaGet<number>('lastSeq')) === undefined) await this.snapshot();
    await this.push();
    await this.pollQuarantine();
    await this.pull();
  }

  // ── push ───────────────────────────────────────────────────────────────────
  private async push(): Promise<void> {
    const c = syncContext();
    for (let round = 0; round < 50; round++) {
      const nowMs = Date.now();
      let ready = (await db().outbox.where('state').equals('pending').sortBy('deviceSeq')).filter((o) => (o.nextTryAt ?? 0) <= nowMs);
      if (c.actor.type === 'staff') ready = ready.filter((o) => o.actor.id === c.actor.id); // a staff login only carries its own entries
      if (!ready.length) return;
      // stop at the first not-yet-ready op so order is preserved
      const batch = ready.slice(0, SYNC_MAX_OPS_PER_PUSH);
      await db().outbox.bulkUpdate(batch.map((o) => ({ key: o.opId, changes: { state: 'sending' as const } })));
      const sentAt = Date.now();
      let res: PushResponse;
      try {
        res = await api.post<PushResponse>('/sync/push', {
          shopId: c.shopId, deviceId: c.deviceId,
          ops: batch.map((o) => ({ opId: o.opId, deviceSeq: o.deviceSeq, shopId: o.shopId, type: o.type, entityId: o.entityId, payload: o.payload, baseVersion: o.baseVersion, changedFields: o.changedFields, clientTs: o.clientTs, actor: o.actor, overrideToken: o.overrideToken, appVersion: o.appVersion })),
        });
      } catch (e) {
        await this.unsend(batch, e);
        throw e;
      }
      await sampleServerTime(res.serverTime, sentAt, Date.now());
      if (res.serverEpoch) await this.noteEpoch(res.serverEpoch);
      await this.applyResults(batch, res);
      if (res.results.length < batch.length) throw new ApiError(0, 'partial_push', '', ''); // the rest is retried
    }
  }

  private async unsend(batch: OutboxItem[], e: unknown): Promise<void> {
    const permanent = e instanceof ApiError && e.status >= 400 && e.status < 500 && ![401, 408, 409, 423, 425, 429].includes(e.status);
    await db().transaction('rw', db().outbox, async () => {
      for (const o of batch) {
        const cur = await db().outbox.get(o.opId);
        if (!cur || cur.state !== 'sending') continue;
        await db().outbox.update(o.opId, permanent
          ? { state: 'failed', error: (e as ApiError).code, tries: cur.tries + 1 }
          : { state: 'pending', tries: cur.tries + 1, nextTryAt: Date.now() + Math.min(SYNC_RETRY_MAX_MS, 1000 * 2 ** cur.tries) * (0.7 + Math.random() * 0.6) });
      }
    });
    if (e instanceof ApiError && e.code === 'upgrade_required') setSync({ needsUpgrade: true });
  }

  private async applyResults(batch: OutboxItem[], res: PushResponse): Promise<void> {
    const d = db();
    const byId = new Map(res.results.map((r) => [r.opId, r]));
    const toRevert: string[] = [];
    await d.transaction('rw', d.outbox, d.sentLog, async () => {
      for (const o of batch) {
        const r = byId.get(o.opId);
        if (!r) { await d.outbox.update(o.opId, { state: 'pending' }); continue; }
        if (r.status === 'applied' || r.status === 'duplicate') {
          await d.outbox.update(o.opId, { state: 'acked', serverSeq: r.serverSeq ?? 0, ackedAt: Date.now(), info: r.info });
          const { undo: _u, localRows: _l, ...plain } = o;
          void _u; void _l;
          await d.sentLog.put({ opId: o.opId, sentAt: Date.now(), op: plain });
        } else if (r.status === 'quarantined') {
          await d.outbox.update(o.opId, { state: 'quarantined', reason: r.reason, info: r.info });
        } else {
          await d.outbox.update(o.opId, { state: 'rejected', reason: r.reason, info: r.info });
          toRevert.push(o.opId);
        }
      }
      await d.sentLog.where('sentAt').below(Date.now() - SENT_LOG_MS).delete();
    });
    for (const id of toRevert) await revertOp(id, false);
    await recomputeOverlay();
  }

  // ── review queue: ask what the Owner decided about parked operations ─────────
  private async pollQuarantine(): Promise<void> {
    const parked = await db().outbox.where('state').equals('quarantined').toArray();
    if (!parked.length) return;
    const ids = parked.map((o) => o.opId).slice(0, 100);
    const out = await api.get<{ items: Array<{ opId: string; status: 'pending' | 'accepted' | 'discarded' }> }>('/sync/quarantine', { query: { opIds: ids.join(',') } });
    for (const it of out.items) {
      if (it.status === 'accepted') await db().outbox.update(it.opId, { state: 'acked', serverSeq: Number.MAX_SAFE_INTEGER, ackedAt: Date.now() });
      else if (it.status === 'discarded') await revertOp(it.opId, true);
    }
  }

  // ── pull ───────────────────────────────────────────────────────────────────
  private async pull(): Promise<void> {
    const c = syncContext();
    const startedAt = Date.now();
    for (let page = 0; page < 200; page++) {
      const after = (await metaGet<number>('lastSeq')) ?? 0;
      const sentAt = Date.now();
      const r = await api.get<PullResponse>('/sync/pull', { query: { shopId: c.shopId, after, limit: SYNC_PULL_DEFAULT_LIMIT } });
      await sampleServerTime(r.serverTime, sentAt, Date.now());
      if (r.serverEpoch && (await this.noteEpoch(r.serverEpoch))) return; // database was restored: start over
      await this.applyPage(r.changes, r.nextAfter);
      if (!r.hasMore) break;
    }
    // everything acknowledged before this pull started is now part of the mirror
    const d = db();
    await d.transaction('rw', d.tables, async () => {
      const done = await d.outbox.where('state').equals('acked').filter((o) => (o.ackedAt ?? 0) < startedAt).toArray();
      for (const o of done) await this.finish(o);
      if (done.length) await recomputeOverlay();
    });
  }

  /** An acknowledged operation is now represented by real server rows: drop its display-only rows and its record. */
  private async finish(o: OutboxItem): Promise<void> {
    const d = db();
    for (const l of o.localRows) await d.table(l.table).delete(l.id);
    // the optimistic marker on the main row goes away when the server row replaced it; make sure nothing keeps the guard
    const guard = GUARDS[o.type];
    if (guard) {
      const row = (await d.table(guard).get(o.entityId)) as Record<string, unknown> | undefined;
      if (row && row._pending) { delete row._pending; await d.table(guard).put(row); }
    }
    await d.outbox.delete(o.opId);
  }

  private async applyPage(changes: PullResponse['changes'], nextAfter: number): Promise<void> {
    const d = db();
    await d.transaction('rw', d.tables, async () => {
      // rows with an unacknowledged local edit keep the user's version
      const open = await d.outbox.where('state').anyOf('pending', 'sending', 'failed', 'quarantined').toArray();
      const blocked = new Set(open.map((o) => (GUARDS[o.type] ? `${GUARDS[o.type]}|${o.entityId}` : '')).filter(Boolean));
      const byTable = new Map<string, { put: Record<string, unknown>[]; del: string[] }>();
      for (const ch of changes) {
        if (!(ch.entity in ENTITY_STORES)) continue;
        const slot = byTable.get(ch.entity) ?? byTable.set(ch.entity, { put: [], del: [] }).get(ch.entity)!;
        if (ch.op === 'delete') { slot.del.push(ch.entityId); continue; }
        if (blocked.has(`${ch.entity}|${ch.entityId}`)) continue;
        const row = ch.data as Record<string, unknown> | null;
        if (row) slot.put.push(normalizeRow(ch.entity, row));
      }
      for (const [table, s] of byTable) {
        if (s.del.length) await d.table(table).bulkDelete(s.del);
        if (s.put.length) await d.table(table).bulkPut(s.put);
      }
      await metaSet('lastSeq', nextAfter);
      const acked = await d.outbox.where('state').equals('acked').filter((o) => (o.serverSeq ?? 0) <= nextAfter).toArray();
      for (const o of acked) await this.finish(o);
      await recomputeOverlay();
      await this.syncCounters(changes);
    });
  }

  /** After pulling, the invoice / return counters must be ahead of anything this device code already used on the server. */
  private async syncCounters(changes: PullResponse['changes']): Promise<void> {
    const code = syncContext().deviceCode;
    if (!code) return;
    let inv = (await metaGet<number>('invoiceSeq')) ?? 0;
    let ret = (await metaGet<number>('returnSeq')) ?? 0;
    for (const ch of changes) {
      const row = ch.data as { deviceCode?: string; deviceSeq?: number; returnNo?: string } | null;
      if (ch.entity === 'sales' && row?.deviceCode === code && typeof row.deviceSeq === 'number') inv = Math.max(inv, row.deviceSeq);
      if (ch.entity === 'sale_returns' && row?.returnNo) { const m = new RegExp(`^R-${code}-(\\d+)$`).exec(row.returnNo); if (m) ret = Math.max(ret, Number(m[1])); }
    }
    await metaSet('invoiceSeq', inv);
    await metaSet('returnSeq', ret);
  }

  // ── snapshot (new device / restored server) ─────────────────────────────────
  async snapshot(): Promise<void> {
    const c = syncContext();
    const d = db();
    const order: string[] = ['settings', ...SNAPSHOT_ENTITIES.filter((e) => e !== 'settings')];
    setSync({ snapshot: { running: true, done: 0, total: order.length } });
    await d.transaction('rw', d.tables, async () => {
      for (const t of Object.keys(ENTITY_STORES)) await d.table(t).clear();
      await metaSet('overlayKeys', []);
    });
    let entity: string | null = 'settings';
    let cursor: string | undefined;
    let firstSeq: number | null = null;
    let guard = 0;
    while (entity && guard++ < 5000) {
      const sentAt = Date.now();
      const page: { entity: string; records: Record<string, unknown>[]; nextCursor: string | null; snapshotSeq: number; nextEntity: string | null; serverEpoch?: string } = await api.get('/sync/snapshot', { query: { shopId: c.shopId, entity, cursor, limit: 1000 } });
      void sentAt;
      if (firstSeq === null) firstSeq = page.snapshotSeq;
      if (page.records.length && page.entity in ENTITY_STORES) await d.table(page.entity).bulkPut(page.records.map((r) => normalizeRow(page.entity, r)));
      if (page.serverEpoch) await metaSet('serverEpoch', page.serverEpoch);
      if (page.nextCursor) { cursor = page.nextCursor; continue; }
      cursor = undefined;
      entity = page.nextEntity;
      setSync({ snapshot: { running: true, done: Math.min(order.length, order.indexOf(entity ?? '') < 0 ? order.length : order.indexOf(entity ?? '')), total: order.length } });
    }
    await metaSet('lastSeq', firstSeq ?? 0);
    await metaSet('needSnapshot', false);
    const settings = await d.settings.toCollection().first();
    if (settings?.deviceCode && settings.deviceCode !== c.deviceCode) c.deviceCode = settings.deviceCode;
    await this.reapplyPending();
    await this.syncCountersFromDb();
    setSync({ snapshot: null, initial: false });
  }

  private async syncCountersFromDb(): Promise<void> {
    const code = syncContext().deviceCode;
    if (!code) return;
    let inv = (await metaGet<number>('invoiceSeq')) ?? 0;
    await db().sales.where('invoiceNo').startsWith(`${code}-`).each((s) => { inv = Math.max(inv, s.deviceSeq); });
    await metaSet('invoiceSeq', inv);
    let ret = (await metaGet<number>('returnSeq')) ?? 0;
    const re = new RegExp(`^R-${code}-(\\d+)$`);
    await db().sale_returns.each((r) => { const m = re.exec(r.returnNo); if (m) ret = Math.max(ret, Number(m[1])); });
    await metaSet('returnSeq', ret);
  }

  /** After the mirror was rebuilt from the server, show the user's unsent work on top of it again. */
  private async reapplyPending(): Promise<void> {
    const d = db();
    const c = syncContext();
    await d.transaction('rw', d.tables, async () => {
      const ops = (await d.outbox.orderBy('deviceSeq').toArray()).filter((o) => o.state !== 'rejected' && o.state !== 'acked');
      for (const op of ops) {
        const ax = new ApplyCtx(d, { opId: op.opId, actor: op.actor, clientTs: op.clientTs, shopId: c.shopId }, bizDate(op.clientTs), c.role);
        await (APPLY[op.type] as (a: ApplyCtx, p: unknown, o: OutboxItem) => Promise<void>)(ax, op.payload, op);
        await d.outbox.update(op.opId, { undo: ax.undo, localRows: ax.local });
      }
      // acknowledged-but-unpulled operations are folded in by the snapshot itself
      await d.outbox.where('state').equals('acked').delete();
      await recomputeOverlay();
    });
  }

  // ── restored server (Guide §21.3) ───────────────────────────────────────────
  /** Returns true when the epoch changed and the mirror was reset. */
  async noteEpoch(epoch: string): Promise<boolean> {
    if (!hasDb()) return false;
    const known = await metaGet<string>('serverEpoch');
    if (!known) { await metaSet('serverEpoch', epoch); return false; }
    if (known === epoch) return false;
    // the server was restored from a backup: replay everything we sent in the last 24 h (the server de-duplicates by opId), then re-download
    const d = db();
    await d.transaction('rw', d.tables, async () => {
      const log = await d.sentLog.where('sentAt').above(Date.now() - SENT_LOG_MS).sortBy('sentAt');
      for (const l of log) {
        if (await d.outbox.get(l.opId)) continue;
        await d.outbox.put({ ...l.op, state: 'pending', tries: 0, undo: [], localRows: [], createdAt: Date.now() } as OutboxItem);
      }
      await d.outbox.where('state').anyOf('sending', 'acked').modify({ state: 'pending' });
      await metaSet('serverEpoch', epoch);
      await metaSet('needSnapshot', true);
    });
    this.kick(0);
    return true;
  }
}

export const engine = new Engine();
export const syncNow = (): Promise<void> => engine.syncNow();
export const kickSync = (ms?: number): void => engine.kick(ms);
